import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { AuthState, User } from "@maeiz/core";
import { isTokenExpired } from "@maeiz/core";
import type { BroadcastConfig } from "@lib/broadcast";
import { OWNER_AUTH_LS_KEY } from "@lib/ownerPersistKeys";

export interface PlatformAuthStore extends AuthState {
    permissions: string[];
    broadcast: BroadcastConfig | null;
    setAuth: (
        user: User,
        token: string,
        permissions: string[],
        broadcast?: BroadcastConfig | null
    ) => void;
    updateUser: (user: User) => void;
    setPermissions: (permissions: string[]) => void;
    setBroadcast: (broadcast: BroadcastConfig | null) => void;
    clearAuth: () => void;
    checkTokenExpiry: () => boolean;
}

const LS_KEY = OWNER_AUTH_LS_KEY;

function syncPersistedState(partial: {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    permissions: string[];
    broadcast: BroadcastConfig | null;
}) {
    try {
        const stored = localStorage.getItem(LS_KEY);
        const parsed = stored ? JSON.parse(stored) : {};
        localStorage.setItem(
            LS_KEY,
            JSON.stringify({
                ...parsed,
                version: typeof parsed.version === "number" ? parsed.version : 0,
                state: {
                    ...parsed.state,
                    ...partial,
                },
            })
        );
    } catch {
        localStorage.setItem(
            LS_KEY,
            JSON.stringify({
                state: partial,
                version: 0,
            })
        );
    }
}

export const useAuthStore = create<PlatformAuthStore>()(
    persist(
        (set, get) => ({
            user: null,
            token: null,
            isAuthenticated: false,
            permissions: [],
            broadcast: null,
            setAuth: (user, token, permissions, broadcast = null) => {
                set({ user, token, isAuthenticated: true, permissions, broadcast });
                const sync = () =>
                    syncPersistedState({
                        user,
                        token,
                        isAuthenticated: true,
                        permissions,
                        broadcast,
                    });
                requestAnimationFrame(() => {
                    sync();
                    setTimeout(sync, 50);
                    setTimeout(sync, 150);
                    setTimeout(sync, 300);
                });
            },
            updateUser: (user) => {
                set((state) => {
                    if (!state.token) return state;
                    syncPersistedState({
                        user,
                        token: state.token,
                        isAuthenticated: true,
                        permissions: state.permissions,
                        broadcast: state.broadcast,
                    });
                    return { user };
                });
            },
            setPermissions: (permissions) => {
                const { user, token, isAuthenticated, broadcast } = get();
                if (!user || !token) {
                    set({ permissions });
                    return;
                }
                set({ permissions });
                syncPersistedState({ user, token, isAuthenticated, permissions, broadcast });
            },
            setBroadcast: (broadcast) => {
                const { user, token, isAuthenticated, permissions } = get();
                set({ broadcast });
                if (!user || !token) return;
                syncPersistedState({ user, token, isAuthenticated, permissions, broadcast });
            },
            clearAuth: () => {
                set({
                    user: null,
                    token: null,
                    isAuthenticated: false,
                    permissions: [],
                    broadcast: null,
                });
                try {
                    localStorage.removeItem(LS_KEY);
                } catch {
                    /* no-op */
                }
            },
            checkTokenExpiry: () => {
                const { token } = get();
                if (!token) return false;
                try {
                    if (isTokenExpired(token)) {
                        get().clearAuth();
                        return false;
                    }
                    return true;
                } catch {
                    return false;
                }
            },
        }),
        {
            name: LS_KEY,
            storage: createJSONStorage(() => localStorage),
            partialize: (s) => ({
                user: s.user,
                token: s.token,
                isAuthenticated: s.isAuthenticated,
                permissions: s.permissions,
                broadcast: s.broadcast,
            }),
        }
    )
);
